import { View, Text, StyleSheet, Platform, ScrollView } from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';
import { createStyles, addColorOpacity } from '../../shared/utils/styles';
import { DesignTokens } from '../../shared/types/tokens';
import { AmortizationScheduleEntry } from '../../shared/types/mortgage';

interface YearByYearSummaryProps {
  schedule: AmortizationScheduleEntry[];
  loanTermYears: number;
}

interface YearSummary {
  year: number;
  startBalance: number;
  endBalance: number;
  principalPaid: number;
  interestPaid: number;
  totalPaid: number;
  paymentsCount: number;
}

export function YearByYearSummary({ schedule, loanTermYears }: YearByYearSummaryProps) {
  const { tokens } = useTheme();
  const utils = createStyles(tokens);
  const styles = makeStyles(tokens, utils);

  // Group payments by year
  const yearSummaries: YearSummary[] = [];
  const paymentsPerYear = 12;
  
  for (let year = 1; year <= loanTermYears; year++) {
    const startPaymentIndex = (year - 1) * paymentsPerYear;
    const endPaymentIndex = Math.min(year * paymentsPerYear, schedule.length);
    
    if (startPaymentIndex >= schedule.length) break;
    
    const yearPayments = schedule.slice(startPaymentIndex, endPaymentIndex);
    if (yearPayments.length === 0) break;
    
    const startBalance = year === 1 
      ? schedule[0].remaining_balance + schedule[0].principal_payment // Initial loan amount
      : schedule[startPaymentIndex - 1]?.remaining_balance || 0;
    
    const endBalance = yearPayments[yearPayments.length - 1].remaining_balance;
    const principalPaid = yearPayments.reduce((sum, p) => sum + p.principal_payment, 0);
    const interestPaid = yearPayments.reduce((sum, p) => sum + p.interest_payment, 0);
    const totalPaid = principalPaid + interestPaid;
    
    yearSummaries.push({
      year,
      startBalance,
      endBalance,
      principalPaid,
      interestPaid,
      totalPaid,
      paymentsCount: yearPayments.length,
    });
  }

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={true}>
      <View style={styles.header}>
        <Text style={styles.title}>Year-by-Year Summary</Text>
        <Text style={styles.subtitle}>
          Principal and interest breakdown by year
        </Text>
      </View>

      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={[styles.tableHeaderCell, styles.yearColumn]}>Year</Text>
          <Text style={[styles.tableHeaderCell, styles.balanceColumn]}>Starting Balance</Text>
          <Text style={[styles.tableHeaderCell, styles.principalColumn]}>Principal Paid</Text>
          <Text style={[styles.tableHeaderCell, styles.interestColumn]}>Interest Paid</Text>
          <Text style={[styles.tableHeaderCell, styles.totalColumn]}>Total Paid</Text>
          <Text style={[styles.tableHeaderCell, styles.balanceColumn]}>Ending Balance</Text>
        </View>

        {yearSummaries.map((summary, idx) => (
          <View key={summary.year} style={[styles.tableRow, idx % 2 === 0 ? styles.tableRowEven : null]}>
            <Text style={[styles.tableCell, styles.yearColumn, styles.yearCell]}>
              {summary.year}
            </Text>
            <Text style={[styles.tableCell, styles.balanceColumn]}>
              ${summary.startBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Text>
            <Text style={[styles.tableCell, styles.principalColumn, styles.principalCell]}>
              ${summary.principalPaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Text>
            <Text style={[styles.tableCell, styles.interestColumn, styles.interestCell]}>
              ${summary.interestPaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Text>
            <Text style={[styles.tableCell, styles.totalColumn, styles.totalCell]}>
              ${summary.totalPaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Text>
            <Text style={[styles.tableCell, styles.balanceColumn]}>
              ${summary.endBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Text>
          </View>
        ))}
      </View>

      {/* Totals Row */}
      <View style={styles.totalsRow}>
        <Text style={styles.totalsLabel}>Total:</Text>
        <Text style={styles.totalsValue}>
          ${yearSummaries.reduce((sum, y) => sum + y.principalPaid, 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Text>
        <Text style={[styles.totalsValue, styles.interestTotal]}>
          ${yearSummaries.reduce((sum, y) => sum + y.interestPaid, 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Text>
        <Text style={styles.totalsValue}>
          ${yearSummaries.reduce((sum, y) => sum + y.totalPaid, 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Text>
      </View>
    </ScrollView>
  );
}

function makeStyles(tokens: DesignTokens, utils: ReturnType<typeof createStyles>) {
  return StyleSheet.create({
    container: {
      flex: 1,
      width: '100%',
    },
    header: {
      marginBottom: utils.spacing.lg,
    },
    title: {
      fontSize: utils.typography.sizes.xl,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onBackground,
      marginBottom: utils.spacing.xs,
      fontFamily: utils.typography.heading.fontFamily,
    },
    subtitle: {
      fontSize: utils.typography.sizes.sm,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      fontFamily: utils.typography.body.fontFamily,
    },
    table: {
      borderWidth: 1,
      borderColor: tokens.color.border,
      borderRadius: utils.radius.lg,
      overflow: 'hidden',
      marginBottom: utils.spacing.lg,
    },
    tableHeader: {
      flexDirection: 'row',
      backgroundColor: tokens.color.primaryContainer,
      borderBottomWidth: 2,
      borderBottomColor: tokens.color.border,
    },
    tableHeaderCell: {
      padding: utils.spacing.sm,
      fontSize: utils.typography.sizes.sm,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onPrimaryContainer,
      fontFamily: utils.typography.body.fontFamily,
      textAlign: 'center',
    },
    tableRow: {
      flexDirection: 'row',
      borderBottomWidth: 1,
      borderBottomColor: tokens.color.border,
    },
    tableRowEven: {
      backgroundColor: addColorOpacity(tokens.color.surface, 0.5),
    },
    tableCell: {
      padding: utils.spacing.sm,
      fontSize: utils.typography.sizes.sm,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
      textAlign: 'center',
    },
    yearColumn: {
      width: 60,
      minWidth: 60,
    },
    balanceColumn: {
      flex: 1.2,
      minWidth: 100,
    },
    principalColumn: {
      flex: 1.2,
      minWidth: 100,
    },
    interestColumn: {
      flex: 1.2,
      minWidth: 100,
    },
    totalColumn: {
      flex: 1.2,
      minWidth: 100,
    },
    yearCell: {
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.primary,
    },
    principalCell: {
      color: tokens.color.success || tokens.color.primary,
      fontWeight: utils.typography.weights.medium,
    },
    interestCell: {
      color: addColorOpacity(tokens.color.onSurface, 0.7),
    },
    totalCell: {
      fontWeight: utils.typography.weights.semibold,
    },
    totalsRow: {
      flexDirection: 'row',
      padding: utils.spacing.md,
      backgroundColor: addColorOpacity(tokens.color.primaryContainer, 0.2),
      borderRadius: utils.radius.md,
      alignItems: 'center',
      gap: utils.spacing.sm,
      flexWrap: 'wrap',
    },
    totalsLabel: {
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
      marginRight: utils.spacing.sm,
    },
    totalsValue: {
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
      flex: 1,
      textAlign: 'right',
    },
    interestTotal: {
      color: addColorOpacity(tokens.color.onSurface, 0.7),
    },
  });
}
